import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Table,
  Alert,
  Spinner,
  Badge,
  InputGroup,
} from 'react-bootstrap';
import {
  FaMoneyBillWave,
  FaPlus,
  FaEdit,
  FaTrash,
  FaArrowLeft,
  FaCalendar,
  FaRupeeSign,
} from 'react-icons/fa';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';

const getAuthToken = () => localStorage.getItem('authToken');

const FeeManagement = () => {
  const { student_id } = useParams();
  const navigate = useNavigate();
  const [fees, setFees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [currentFee, setCurrentFee] = useState({
    id: null,
    amount: '',
    date: '',
    remark: '',
  });

  useEffect(() => {
    fetchStudentDetails();
  }, []);

  useEffect(() => {
    if (student) fetchFees();
  }, [student]);

  const fetchStudentDetails = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-student`,
        { id: student_id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setStudent(response.data.data);
      } else {
        setAlert({
          show: true,
          message: 'Failed to fetch student details.',
          variant: 'danger',
        });
        navigate(-1);
      }
    } catch (err) {
      setAlert({
        show: true,
        message: 'Error fetching student details.',
        variant: 'danger',
      });
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchFees = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      navigate('/login');
      return;
    }
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-student-fees`,
        { student_id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setFees(response.data.fees || []);
      } else {
        setAlert({
          show: true,
          message: 'Failed to fetch fees.',
          variant: 'danger',
        });
      }
    } catch (error) {
      console.error('Error fetching fees:', error);
      setAlert({
        show: true,
        message: 'Error loading fees. Please try again.',
        variant: 'danger',
      });
    }
  };

  const handleShowModal = (fee = {}) => {
    setCurrentFee({
      id: fee.id || null,
      amount: fee.amount || '',
      date: fee.date || '',
      remark: fee.remark || '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentFee({ id: null, amount: '', date: '', remark: '' });
  };

  const handleSaveFee = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        setAlert({
          show: true,
          message: 'Unauthorized! Please log in.',
          variant: 'danger',
        });
        navigate('/login');
        return;
      }

      const payload = { ...currentFee, student_id: student.id };
      const url = currentFee.id
        ? `${appConstants.API_ROOT_URL}update-fee`
        : `${appConstants.API_ROOT_URL}create-fee`;

      const response = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === 'success') {
        setAlert({
          show: true,
          message: response.data.message || 'Fee saved successfully!',
          variant: 'success',
        });
        setTimeout(
          () => setAlert({ show: false, message: '', variant: '' }),
          3000,
        );
        fetchFees();
        handleCloseModal();
      } else {
        setAlert({
          show: true,
          message: response.data.message || 'Failed to save fee.',
          variant: 'danger',
        });
      }
    } catch (error) {
      console.error('Error saving fee:', error);
      setAlert({
        show: true,
        message:
          error.response?.data?.message || 'An unexpected error occurred.',
        variant: 'danger',
      });
    }
  };

  const handleDeleteFee = async (id) => {
    if (!window.confirm('Are you sure you want to delete this fee?')) {
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        setAlert({
          show: true,
          message: 'Unauthorized! Please log in.',
          variant: 'danger',
        });
        navigate('/login');
        return;
      }

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}delete-fee`,
        { id },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        setAlert({
          show: true,
          message: response.data.message || 'Fee deleted successfully!',
          variant: 'success',
        });
        setTimeout(
          () => setAlert({ show: false, message: '', variant: '' }),
          3000,
        );
        fetchFees();
      } else {
        setAlert({
          show: true,
          message: response.data.message || 'Failed to delete fee.',
          variant: 'danger',
        });
      }
    } catch (error) {
      console.error('Error deleting fee:', error);
      setAlert({
        show: true,
        message:
          error.response?.data?.message || 'An unexpected error occurred.',
        variant: 'danger',
      });
    }
  };

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '30px 20px',
        }}
      >
        {/* Header Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            color: 'white',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <div>
              <h4
                style={{
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <FaMoneyBillWave style={{ fontSize: '1.8rem' }} />
                Fee Management
              </h4>
              <p
                style={{
                  margin: '8px 0 0 0',
                  opacity: 0.9,
                  fontSize: '1.1rem',
                }}
              >
                {student ? student.name : 'Loading...'}
                {student?.admission_no && (
                  <Badge
                    bg="light"
                    text="dark"
                    style={{ marginLeft: '10px', fontSize: '0.9rem' }}
                  >
                    {student.admission_no}
                  </Badge>
                )}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <Button
                variant="light"
                onClick={() => handleShowModal()}
                style={{
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FaPlus /> Add Fee
              </Button>
              <Button
                variant="outline-light"
                onClick={() => navigate(-1)}
                style={{
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FaArrowLeft /> Back
              </Button>
            </div>
          </div>
        </div>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Fees Table Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spinner animation="border" variant="primary" />
              <p style={{ marginTop: '15px', color: '#6c757d' }}>
                Loading fees...
              </p>
            </div>
          ) : fees.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#6c757d',
              }}
            >
              <FaMoneyBillWave
                style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '15px' }}
              />
              <p>No fees found</p>
              <Button
                variant="primary"
                onClick={() => handleShowModal()}
                style={{
                  marginTop: '10px',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: '600',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                }}
              >
                <FaPlus /> Add First Fee
              </Button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Table hover responsive style={{ marginBottom: 0 }}>
                <thead
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  <tr>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        width: '8%',
                      }}
                    >
                      #
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        width: '20%',
                      }}
                    >
                      Amount
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        width: '20%',
                      }}
                    >
                      Date
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        width: '37%',
                      }}
                    >
                      Remark
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        width: '15%',
                        textAlign: 'center',
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fees.map((fee, index) => (
                    <tr
                      key={fee.id}
                      style={{
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <td style={{ padding: '12px', fontSize: '0.9rem' }}>
                        {index + 1}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: '#28a745',
                        }}
                      >
                        ₹
                        {parseFloat(fee.amount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td style={{ padding: '12px', fontSize: '0.9rem' }}>
                        {fee.date}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          fontSize: '0.9rem',
                          color: '#6c757d',
                        }}
                      >
                        {fee.remark || '-'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            justifyContent: 'center',
                          }}
                        >
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowModal(fee)}
                            title="Edit Fee"
                            style={{ borderRadius: '6px', padding: '6px 12px' }}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteFee(fee.id)}
                            title="Delete Fee"
                            style={{ borderRadius: '6px', padding: '6px 12px' }}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Adding/Editing Fee */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderBottom: 'none',
          }}
        >
          <Modal.Title
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <FaMoneyBillWave />
            {currentFee.id ? 'Edit Fee' : 'Add Fee'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px' }}>
          <Form>
            <Form.Group style={{ marginBottom: '20px' }}>
              <Form.Label
                style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '8px',
                }}
              >
                Amount <span style={{ color: '#dc3545' }}>*</span>
              </Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: 'white',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                  }}
                >
                  <FaRupeeSign style={{ color: '#6c757d' }} />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={currentFee.amount}
                  onChange={(e) =>
                    setCurrentFee({ ...currentFee, amount: e.target.value })
                  }
                  placeholder="Enter amount"
                  style={{
                    borderLeft: 'none',
                    borderRadius: '0 8px 8px 0',
                    paddingLeft: '0',
                  }}
                />
              </InputGroup>
            </Form.Group>
            <Form.Group style={{ marginBottom: '20px' }}>
              <Form.Label
                style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '8px',
                }}
              >
                Date <span style={{ color: '#dc3545' }}>*</span>
              </Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: 'white',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                  }}
                >
                  <FaCalendar style={{ color: '#6c757d' }} />
                </InputGroup.Text>
                <Form.Control
                  type="date"
                  value={currentFee.date}
                  onChange={(e) =>
                    setCurrentFee({ ...currentFee, date: e.target.value })
                  }
                  style={{
                    borderLeft: 'none',
                    borderRadius: '0 8px 8px 0',
                    paddingLeft: '0',
                  }}
                />
              </InputGroup>
            </Form.Group>
            <Form.Group style={{ marginBottom: '0' }}>
              <Form.Label
                style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '8px',
                }}
              >
                Remark
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={currentFee.remark}
                onChange={(e) =>
                  setCurrentFee({ ...currentFee, remark: e.target.value })
                }
                placeholder="Enter remark (optional)"
                style={{ borderRadius: '8px' }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer
          style={{ borderTop: 'none', padding: '0 30px 30px 30px' }}
        >
          <Button
            variant="outline-secondary"
            onClick={handleCloseModal}
            style={{
              borderRadius: '8px',
              padding: '10px 24px',
              fontWeight: '600',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleSaveFee}
            style={{
              borderRadius: '8px',
              padding: '10px 24px',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
            }}
          >
            {currentFee.id ? 'Update' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default FeeManagement;
